import { useState } from "react";
import { Controller, Control, FieldPath, FieldValues } from "react-hook-form";
import { Input, TextField, Label } from "react-aria-components";

type PasswordInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: "current-password" | "new-password";
};

const PasswordInput = <T extends FieldValues>({
  name,
  control,
  label = "Password",
  placeholder = "Enter your password",
  description,
  required = false,
  disabled = false,
  autoComplete = "current-password",
}: PasswordInputProps<T>) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required ? "Password is required." : false,
        minLength: {
          value: 6,
          message: "Password must be at least 6 characters.",
        },
      }}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { invalid, error, isDirty, isTouched },
      }) => {
        return (
          <TextField
            isInvalid={invalid}
            isRequired={required}
            isDisabled={disabled}
          >
            <Label className="-mb-1 block text-base font-semibold text-evergreen">
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            {description && (
              <div className="mb-2 text-sm text-gray-600">{description}</div>
            )}
            <Input
              ref={ref}
              type="password"
              value={value || ""}
              onChange={onChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                onBlur();
                setIsFocused(false);
              }}
              disabled={disabled}
              className="block w-full rounded-none border-b border-gray-400 bg-transparent py-2 text-2xl font-bold text-soil transition-colors placeholder:text-soil placeholder:text-opacity-25 focus:border-cantelope focus:shadow-focus focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={placeholder}
              autoComplete={autoComplete}
            />
            {error?.message &&
              isDirty &&
              isTouched &&
              !isFocused &&
              !disabled && (
                <div className="mt-2 text-sm text-red-700">{error.message}</div>
              )}
          </TextField>
        );
      }}
    />
  );
};

export default PasswordInput;
